import React, { useState, useCallback, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { axios, context as AuthContext } from '../../context/auth'
import { SecondaryButton } from '../../styled/button'
import Select from '../../styled/select'

const ShareContainer = styled.div`
  display: inline-block;
  position: relative;
`

const ShareDropdownContainer = styled.div`
  margin-right: 0.5rem;
  min-width: 200px;
  display: inline-block;
`

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 4px;
  background-color: ${props => props.error ? '#f8d7da' : '#d4edda'};
  border: 1px solid ${props => props.error ? '#f5c6cb' : '#c3e6cb'};
  color: ${props => props.error ? '#721c24' : '#155724'};
  z-index: 1000;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`

const SetlistShareButton = ({ setlistId, setlist }) => {
  const { user } = useContext(AuthContext)
  const [groupsWithWebhooks, setGroupsWithWebhooks] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [toast, setToast] = useState(null)

  useEffect(() => {
    const fetchGroupsWithWebhooks = async () => {
      try {
        const response = await axios.get('/api/groups')
        const groups = response.data || []

        // Filter groups that have webhook enabled and user is a member
        const webhookGroups = groups.filter(group =>
          group.webhookConfig &&
          group.webhookConfig.enabled &&
          group.webhookConfig.webhookUrl
        )

        setGroupsWithWebhooks(webhookGroups)
        if (webhookGroups.length > 0) {
          setSelectedGroupId(webhookGroups[0]._id)
        }
      } catch (error) {
        console.error('Error fetching groups:', error)
      }
    }

    if (user) {
      fetchGroupsWithWebhooks()
    }
  }, [user])

  const showToast = useCallback((message, isError = false) => {
    setToast({ message, error: isError })
    setTimeout(() => setToast(null), 5000)
  }, [])

  const handleShare = useCallback(async () => {
    if (!selectedGroupId) {
      showToast('Please select a group', true)
      return
    }

    setIsLoading(true)

    try {
      await axios.post(`/api/setlists/${setlistId}/share`, {
        groupId: selectedGroupId
      })

      const selectedGroup = groupsWithWebhooks.find(g => g._id === selectedGroupId)
      const platform = selectedGroup?.webhookConfig?.platform || 'messaging platform'
      showToast(`Setlist shared to ${platform} successfully!`)
    } catch (error) {
      showToast(error.response?.data?.msg || 'Failed to share setlist', true)
    } finally {
      setIsLoading(false)
    }
  }, [setlistId, selectedGroupId, groupsWithWebhooks, showToast])

  if (groupsWithWebhooks.length === 0) {
    return null
  }

  const selectedGroup = groupsWithWebhooks.find(g => g._id === selectedGroupId)
  const platform = selectedGroup?.webhookConfig?.platform || ''
  const buttonText = platform === 'slack' ? 'Send to Slack' : platform === 'discord' ? 'Send to Discord' : 'Share'

  return (
    <>
      <ShareContainer>
        {groupsWithWebhooks.length > 1 && (
          <ShareDropdownContainer>
            <Select
              value={selectedGroupId ? { value: selectedGroupId, label: `${groupsWithWebhooks.find(g => g._id === selectedGroupId)?.title} (${groupsWithWebhooks.find(g => g._id === selectedGroupId)?.webhookConfig?.platform})` } : null}
              onChange={(option) => setSelectedGroupId(option ? option.value : '')}
              options={groupsWithWebhooks.map(group => ({ value: group._id, label: `${group.title} (${group.webhookConfig.platform})` }))}
              isDisabled={isLoading}
            />
          </ShareDropdownContainer>
        )}
        <SecondaryButton onClick={handleShare} disabled={isLoading}>
          {isLoading ? 'Sharing...' : buttonText}
        </SecondaryButton>
      </ShareContainer>

      {toast && (
        <Toast error={toast.error}>
          {toast.message}
        </Toast>
      )}
    </>
  )
}

export default SetlistShareButton
